import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useSearchParams } from "react-router";
import {
  MyDocumentGroupsDocument,
  MyDocumentsDocument,
} from "@/graphql/generated/operations";
import { SERVICE_INPUTS, SERVICE_OUTPUTS } from "@/pages/save/service/service-actions";
import { useServiceActions } from "@/pages/save/service/use-service-actions";
import { ServiceMenuPopover, type ServiceMenuGroup } from "./ServiceMenuPopover";

const GROUPS: readonly ServiceMenuGroup[] = [
  { titleId: "service.page.inputTitle", cards: SERVICE_INPUTS },
  { titleId: "service.page.outputTitle", cards: SERVICE_OUTPUTS, accent: true },
];

// Server page size for the document walk below, as on the Documents page.
const PAGE_FETCH = 100;

// Profile-header launcher for the Documents Input/Output actions. Renders the
// real ServiceCards in a popover and opens each flow's dialog IN PLACE on the
// profile (no navigation) via the shared useServiceActions hook.
export function ServiceMenu() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Seed "open" from the deep link (?service=open) at first render, so arriving
  // from the Settings link shows the cards without ever calling setState in an
  // effect just to open. The param is stripped below.
  const [openAtStart] = useState(() => searchParams.get("service") === "open");
  const [actionError, setActionError] = useState<string | null>(null);
  // Latched, not tied to `open`: opening a card CLOSES the popover and mounts
  // the dialog, which still needs the documents.
  const [docsNeeded, setDocsNeeded] = useState(() => searchParams.get("service") === "open");

  // The extraction dialog needs the user's documents to pick from; the profile
  // doesn't otherwise load them. ALL of them: a document past the first page
  // was simply absent from the analysis, with nothing on screen saying so.
  // Deferred until someone reaches for the menu — every node carries its card
  // thumbnail, which no reader of the profile page needs.
  const {
    data: docData,
    loading: docsFetching,
    refetch: docsRefetch,
    fetchMore: docsFetchMore,
  } = useQuery(MyDocumentsDocument, {
    variables: { input: { first: PAGE_FETCH } },
    skip: !docsNeeded,
  });
  // Each appended page (merged by the cursor field policy in graphql/client.ts)
  // advances pageInfo, re-running this until no page is left.
  const docsPageInfo = docData?.documents?.pageInfo;
  useEffect(() => {
    if (docsPageInfo?.hasNextPage && docsPageInfo.endCursor) {
      // A rejected page leaves the list incomplete, which `docsLoading` below
      // keeps saying; what it must not do is surface as an unhandled rejection.
      void docsFetchMore({
        variables: { input: { first: PAGE_FETCH, after: docsPageInfo.endCursor } },
      }).catch(() => {});
    }
  }, [docsPageInfo, docsFetchMore]);
  const { data: groupsData } = useQuery(MyDocumentGroupsDocument, { variables: { input: {} } });
  const docs = useMemo(
    () =>
      (docData?.documents?.nodes ?? []).map((d) => ({
        id: d.id,
        name: d.name ?? "",
        groupId: (d.group?.id as string | null) ?? null,
        createdAt: (d.createdAt as string | null) ?? null,
      })),
    [docData],
  );
  // DocumentGroupOutput is a single-member union - unwrap via the any seam.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const groupsOut: any = groupsData?.documentGroups;
  const groups = (groupsOut?.documentGroups ?? []) as readonly { id: string; name: string }[];

  const { runServiceAction, busyActions, dialogs } = useServiceActions({
    docs,
    groups,
    // Until every page is in, an absent document means "not yet", not "gone".
    docsLoading: docsNeeded && (docsFetching || !docData || !!docsPageInfo?.hasNextPage),
    // Only refresh a stock that was actually loaded: the ?service=linkedin deep
    // link runs a dialog without ever asking for the documents.
    onChanged: () => {
      if (docsNeeded) void docsRefetch();
    },
    onError: setActionError,
  });

  // Deep links: ?service=open (Settings "data transfer" link) seeded `open` above;
  // ?service=linkedin (tour follow-up) starts the LinkedIn import directly — it only opens a
  // dialog, so auto-running is safe. Everything else only ever OPENS the menu (an auto-run
  // export would download unprompted). Stripping the param right away keeps this idempotent
  // (the re-run sees no param) AND re-armed: the component stays mounted on the profile page,
  // and a later navigation that sets the param again (clicking the tour chip a second time)
  // must fire again — no once-per-mount latch.
  useEffect(() => {
    const value = searchParams.get("service");
    if (value !== "open" && value !== "linkedin") return;
    const next = new URLSearchParams(searchParams);
    next.delete("service");
    setSearchParams(next, { replace: true });
    if (value === "linkedin") runServiceAction("linkedin");
  }, [searchParams, setSearchParams, runServiceAction]);

  return (
    <>
      {/* Hover zone = button + popover only. The dialogs render OUTSIDE it:
          a dialog is a full-screen modal, and if it were a descendant here,
          hovering it would re-fire onMouseEnter and re-open the popover behind
          the modal (close/re-open flicker). */}
      <ServiceMenuPopover
        groups={GROUPS}
        busyActions={busyActions}
        defaultOpen={openAtStart}
        onOpen={() => setDocsNeeded(true)}
        onAction={(spec) => spec.action && runServiceAction(spec.action)}
        note={actionError ? <span className="text-danger">{actionError}</span> : null}
      />
      {dialogs}
    </>
  );
}
